var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ReusableLogicConfig_1;
import { BoolToolParameter, CalendarToolParameter, ColumnToolParameter, DateToolParameter, EnumToolParameter, NumberToolParameter, ReusableLogicForbiddenToolTypes, SimpleToolVariable, StringToolParameter, ToolParameter, ToolParameterType, ToolParameterValueType, ToolType, ToolVariable } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonConverter, JsonDiscriminatorValue, JsonSchemaV2, MapConverter, MultiValueMapConverter } from '@symon-ai/wisepipe-serialize';
import { EdgeElement } from './edgeElement';
import { Graph } from './graph';
import { ReusableLogicInputConfig } from './reusableLogicInputConfig';
import './reusableLogicInputMapping'; // force import for JsonSchemaV2
import { ReusableLogicInputMapping } from './reusableLogicInputMapping';
import { ReusableLogicOutputConfig } from './reusableLogicOutputConfig';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { reusableLogicConfigSchemaValidation } from '../validations.generated';
export class ReusableLogicDependantID {
    // reusableLogicID - Reusable Logic ID of parent RL
    // elementID = element ID of the parent RL node
    constructor(reusableLogicID, elementID) {
        this.reusableLogicID = reusableLogicID;
        this.elementID = elementID;
    }
    toString() {
        return `${this.reusableLogicID}_${this.elementID}`;
    }
}
export const reusableLogicConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:ReusableLogicConfig',
    type: 'object',
    properties: {
        reusableLogicID: { type: 'string', uid: 9 },
        inputMap: {
            type: 'object',
            additionalProperties: { $ref: 'urn:wisepipe-common-pipe:ReusableLogicInputMapping' }
        },
        outputMap: {
            type: 'object',
            additionalProperties: { type: 'string' }
        },
        parameterValues: {
            type: 'object',
            additionalProperties: {
                anyOf: [{ $ref: 'urn:wisepipe-base-pipe:SimpleToolVariable' }, { $ref: 'urn:wisepipe-base-pipe:ToolParameter' }]
            }
        }
    },
    required: ['reusableLogicID', 'inputMap', 'outputMap']
};
let ReusableLogicConfig = ReusableLogicConfig_1 = class ReusableLogicConfig extends ToolConfig {
    constructor(reusableLogicID, inputMap, outputMap, parameterValues, customFormats) {
        super(ToolType.REUSABLE_LOGIC, customFormats);
        this.reusableLogicID = reusableLogicID;
        this.parameterValues = parameterValues;
        this.inputMap = inputMap;
        this.outputMap = outputMap;
        JSON.validate(this, ReusableLogicConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    multiIns(inScope) {
        return [...inScope.names];
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return ReusableLogicConfig_1.ins;
    }
    get outs() {
        return Array.from(this.outputMap.values());
    }
    // still need static ins for info().
    // use instance method multiIns() to get actual input port names
    static get ins() {
        return ['multi'];
    }
    static get outs() {
        return ['multi'];
    }
    static get info() {
        return new ToolConfigInfo('Tool', [ToolCategories.REUSABLE_LOGIC, ToolCategories.UNSEEN], this.ins, this.outs, ['reuse', 'refactor', 'reusable', 'logic'], true // sparkAvailable true by default, but could be false depending on elements within RL
        );
    }
    getOutput(iConfigHelper, ins) {
        const reusableLogic = ins.reusableLogic;
        // Sanity check
        if (reusableLogic?.reusableLogicID !== this.reusableLogicID) {
            throw new Error("ReusableLogicConfig.getOutput: ReusableLogicConfig does not match ToolScope's reusableLogic");
        }
        const rlGraph = new Graph(reusableLogic.getElements());
        const rlInputNodes = rlGraph.reusableLogicInputNodes();
        const rlOutputNodes = rlGraph.reusableLogicOutputNodes();
        // all dependeeIns are mapped to a new datasource. overwrite the dependeeIn's scope
        rlInputNodes.forEach((rlInputNode) => {
            const inputMapping = this.inputMap.get(rlInputNode.elementID);
            const port = this.getPortName(inputMapping);
            const tsb = new ToolScopeBuilder(ins);
            const source = ins.schemas.at(port);
            // create renaming map. where dependant col names are old names, and dependee col names are new names
            const renameMap = inputMapping.columnMappings.reduce((result, { dependantColumnName, dependeeColumnName }) => {
                if (!result[dependantColumnName]) {
                    result[dependantColumnName] = [];
                }
                result[dependantColumnName].push(dependeeColumnName);
                return result;
            }, {});
            const exp = tsb.export(ReusableLogicInputConfig.out);
            source.forEach((s) => {
                // cols not in renameMap are dropped
                if (renameMap[s.name]) {
                    // cols in renameMap are renamed. if theres more than one in array, make multiple copies
                    renameMap[s.name].forEach((newName) => {
                        exp.copy(port, s, false, true, undefined, newName);
                    });
                }
            });
            // Setting the output tool scope for the RL input node
            ins.reusableLogicMeta.setOutput(rlInputNode.elementID, tsb.build());
        });
        // get scope for each output
        const outs = rlOutputNodes.map((rlOutputNode) => {
            const outPort = this.outputMap.get(rlOutputNode.elementID);
            const outputIns = ins.reusableLogicMeta.getMetadata(rlOutputNode.elementID).ins;
            const tsb = new ToolScopeBuilder(outputIns);
            tsb.setReusableLogicID(ins.reusableLogicID).setReusableLogicDependants(ins.reusableLogicDependants);
            tsb
                .export(outPort)
                .copyAll(ReusableLogicOutputConfig.in)
                .initializeColumnFormats()
                .setColumnFormats(this.customFormats);
            const ts = tsb.build();
            return ts;
        });
        const tsb = new ToolScopeBuilder(...outs);
        const ts = tsb.exportAll().build();
        return ts;
    }
    isValidForCalc(iConfigHelper, scope) {
        const reusableLogic = scope.reusableLogic;
        const reusableLogicMeta = scope.reusableLogicMeta;
        if (!reusableLogic || !reusableLogicMeta) {
            return ToolValidationState.BAD_METADATA;
        }
        // Sanity check
        if (reusableLogic?.reusableLogicID !== this.reusableLogicID) {
            throw new Error("ReusableLogicConfig.isValidForCalc: ReusableLogicConfig does not match ToolScope's reusableLogic");
        }
        // Check for cycles
        if (scope.reusableLogicID === reusableLogic.reusableLogicID ||
            scope.reusableLogicDependants.some((d) => d.reusableLogicID === reusableLogic.reusableLogicID)) {
            return ToolValidationState.REUSABLE_LOGIC_CYCLE;
        }
        const rlParameterDefinitions = reusableLogic.parameters;
        // Ensure all parameter definitions have values defined
        if (rlParameterDefinitions && this.parameterValues) {
            // All parameter definitions have a default value, so it's possible, and valid, to have parameter defintions with no matching parameter value.
            // In this case, the default value would be used.
            // Ensure there are no extra parameter values that are not included in parameter definitions.
            if ([...this.parameterValues.keys()].some((paramName) => !rlParameterDefinitions.some((paramDef) => {
                return paramDef.name === paramName;
            }))) {
                return ToolValidationState.REUSABLE_LOGIC_EXTRA_PARAMETER_VALUE;
            }
            // Ensure all parameter value types match with the parameter definitions.
            if (!rlParameterDefinitions.every((paramDef) => {
                const paramValue = this.parameterValues.get(paramDef.name);
                if (paramValue === undefined)
                    return true; // parameterValue isn't present, so the definition default would be used, which is valid
                const paramValueType = paramValue instanceof SimpleToolVariable ? paramValue.expectedValueType : paramValue.valueType;
                return paramValueType === paramDef.valueType;
            })) {
                return ToolValidationState.REUSABLE_LOGIC_PARAMETER_VALUE_TYPE_MISMATCH;
            }
        }
        const rlGraph = new Graph(reusableLogic.getElements());
        const rlInputNodes = rlGraph.reusableLogicInputNodes();
        const rlOutputNodes = rlGraph.reusableLogicOutputNodes();
        // Ensure there is at least one input node and one output node in the RL
        if (rlInputNodes.length === 0 || rlOutputNodes.length === 0) {
            return ToolValidationState.BAD_METADATA;
        }
        // Ensure all Input nodes of the RL exist as a key in the inputMap, and all keys in the inputMap exist as inputNodes in the RL
        if (!rlInputNodes.every((inputNode) => this.inputMap.has(inputNode.elementID)) ||
            this.inputMap.size !== rlInputNodes.length) {
            return ToolValidationState.REUSABLE_LOGIC_UNMAPPED_INPUT;
        }
        // Ensure all Output nodes are mapped to an output port in the outputMap, and all keys in the outputMap exist as outputNodes in the RL
        if (!rlOutputNodes.every((outputNode) => this.outputMap.has(outputNode.elementID)) ||
            this.outputMap.size !== rlOutputNodes.length) {
            return ToolValidationState.REUSABLE_LOGIC_UNMAPPED_OUTPUT;
        }
        // Ensure there are no duplicate output ports
        const outputPorts = Array.from(this.outputMap.values());
        if (new Set(outputPorts).size !== outputPorts.length) {
            return ToolValidationState.INVALID;
        }
        // Ensure that all ports in inputMap config are consistent with ports in metadata
        const inputNames = scope.names;
        const portsInConfig = Array.from(this.inputMap.values()).map((input) => {
            return this.getPortName(input);
        });
        if (!portsInConfig.every((portInConfig) => inputNames.has(portInConfig))) {
            return ToolValidationState.REUSABLE_LOGIC_MAPPED_INPUT_MISSING;
        }
        // Ensure all expected columns are mapped correctly
        const badColumnMappings = this.getDataForBadColumnMappings(rlGraph, scope);
        if (badColumnMappings?.length) {
            return ToolValidationState.REUSABLE_LOGIC_MAPPED_COLUMN_INVALID;
        }
        // Restriction for now. Because the dependant columns become columns in a SelectConfig in reusableLogicProcessor.
        const hasDupeColumns = rlInputNodes.some((rlInputNode) => {
            const inputMapping = this.inputMap.get(rlInputNode.elementID);
            const dependantColumns = inputMapping.columnMappings.map((columnMapping) => columnMapping.dependantColumnName ?? '');
            return dependantColumns.length !== new Set(dependantColumns).size;
        });
        if (hasDupeColumns) {
            return ToolValidationState.INVALID;
        }
        // all dependeeIns are mapped to a new datasource. overwrite the dependeeIn's scope
        rlInputNodes.forEach((rlInputNode) => {
            const inputMapping = this.inputMap.get(rlInputNode.elementID);
            const port = this.getPortName(inputMapping);
            const tsb = new ToolScopeBuilder(scope);
            const source = scope.schemas.at(port);
            // create renaming map. where dependant col names are old names, and dependee col names are new names
            const renameMap = inputMapping.columnMappings.reduce((result, { dependantColumnName, dependeeColumnName }) => {
                if (!result[dependantColumnName]) {
                    result[dependantColumnName] = [];
                }
                result[dependantColumnName].push(dependeeColumnName);
                return result;
            }, {});
            const exp = tsb.export(ReusableLogicInputConfig.out);
            source.forEach((s) => {
                // cols not in renameMap are dropped
                if (renameMap[s.name]) {
                    // cols in renameMap are renamed. if theres more than one in array, make multiple copies
                    renameMap[s.name].forEach((newName) => {
                        exp.copy(port, s, false, true, undefined, newName);
                    });
                }
            });
            // Setting the output tool scope for the RL input node
            reusableLogicMeta.setOutput(rlInputNode.elementID, tsb.build());
        });
        // validate all nodes in the inner pipe
        for (const n of rlGraph.getComputeOrder()) {
            if (ReusableLogicForbiddenToolTypes.includes(n.toolType)) {
                return ToolValidationState.REUSABLE_LOGIC_FORBIDDEN_TOOL;
            }
            const parameterMap = iConfigHelper.getParameters();
            const state = this.scopeParametersToReusableLogic(parameterMap, reusableLogic, () => {
                const ins = reusableLogicMeta.getMetadata(n.elementID).ins;
                if (!n.config) {
                    return ToolValidationState.EMPTY;
                }
                const valid = n.config.isValidForCalc(iConfigHelper, ins);
                if (valid === ToolValidationState.REUSABLE_LOGIC_CYCLE) {
                    return valid;
                }
                if (valid !== ToolValidationState.VALID) {
                    return ToolValidationState.REUSABLE_LOGIC_INNER_INVALID;
                }
                return ToolValidationState.VALID;
            });
            if (state !== ToolValidationState.VALID) {
                return state;
            }
        }
        return ToolValidationState.VALID;
    }
    isValidForRun(iConfigHelper, scope, pass) {
        return this.isValidForCalc(iConfigHelper, scope);
    }
    // eslint-disable-next-line class-methods-use-this
    getPortName(input) {
        const port = input.previousToolOutputPort ? `${input.port}_${input.previousToolOutputPort}` : input.port;
        return port;
    }
    getDataForBadColumnMappings(reusableLogicElementsGraph, scope) {
        const inputNodes = reusableLogicElementsGraph.reusableLogicInputNodes();
        const result = [];
        // check that all dependee columns are mapped properly to an incoming column
        for (const inputNode of inputNodes) {
            const inputNodeColumns = inputNode.config?.inputColumns;
            if (inputNodeColumns) {
                const inputMapping = this.inputMap.get(inputNode.elementID);
                for (const inputNodeColumn of inputNodeColumns) {
                    const mappedOccurances = inputMapping.columnMappings.filter((c) => c.dependeeColumnName === inputNodeColumn.name);
                    if (mappedOccurances.length !== 1 || !mappedOccurances[0].dependantColumnName) {
                        // column should be mapped exactly once and mapped to a non-empty string
                        result.push(inputMapping);
                    }
                    else {
                        // check that dependee col type is consistent with mapped dependant col type
                        const port = this.getPortName(inputMapping);
                        const dependantCol = scope.schemas
                            .at(port)
                            ?.find((c) => c.name === mappedOccurances[0].dependantColumnName);
                        if (!dependantCol) {
                            // mapped column not found
                            result.push(inputMapping);
                        }
                        else if (!ToolConfig.columnTypesAreEqual(dependantCol.type, inputNodeColumn.type)) {
                            // column types don't match
                            result.push(inputMapping);
                        }
                    }
                }
            }
        }
        return result;
    }
    /**
     * special edge case: There can only be one edge between two elements (UI restriction). However if an edge is between a multi-output tool and a multi-input tool,
     * there may be multiple connections represented by one edge. This function returns all connections represented by one edge.
     */
    getAllConnectionsfromEdge(edge) {
        // multiEdge here means multiple connections between ports of source and target nodes although there is only one physical edge in graph
        const multiEdges = Array.from(this.inputMap.values()).filter((input) => input.port === edge.tarPort && input.previousToolOutputPort);
        const virtualEdgeElements = multiEdges.map((e) => {
            return new EdgeElement('A'.repeat(14), // just a temporary fake edge so ID doesn't matter
            edge.srcElementID, e.previousToolOutputPort, edge.tarElementID, this.getPortName(e));
        });
        return virtualEdgeElements;
    }
    // This method temporarily modifies the ToolParameterMap in place, replacing the reusable logic parameter with those from this config.
    // The supplied callback function should use the same ToolParameterMap variable within its closure as passed in to this function , as it will be modified in place.
    // The original ToolParameterMap will be restored after the callback function has been executed.
    // We scope the parameters like this in case there are multiple RL nodes with repeated parameters names in the same pipe, so parameters don't overwrite each other.
    scopeParametersToReusableLogic(originalToolParameters, reusableLogic, scopedFunction) {
        if (this.reusableLogicID !== reusableLogic.reusableLogicID) {
            throw new Error('ReusableLogicConfig.scopeParametersToReusableLogic: reusableLogicID in config does not match ReusableLogic');
        }
        const oldScopeReusableParameters = originalToolParameters.replace(ToolParameterType.REUSABLE, this.generateReusableLogicParameters(originalToolParameters, reusableLogic));
        const oldScopeToolParameters = originalToolParameters.replace(ToolParameterType.TOOL, this.generateReusableLogicParameters(originalToolParameters, reusableLogic));
        const result = scopedFunction();
        originalToolParameters.replace(ToolParameterType.REUSABLE, oldScopeReusableParameters);
        originalToolParameters.replace(ToolParameterType.TOOL, oldScopeToolParameters);
        return result;
    }
    generateReusableLogicParameters(parameters, rl) {
        const defaultParams = rl.parameters;
        const scopeParams = [];
        if (this.parameterValues === undefined && !defaultParams)
            return [];
        defaultParams?.forEach((param) => {
            const userDefinedParam = this.parameterValues?.get(param.name);
            switch (param.valueType) {
                case ToolParameterValueType.STRING: {
                    const typedParam = param;
                    if (typedParam.defaultValue === undefined || typedParam.defaultValue === null)
                        throw new Error(`StringReusableLogicParameterDefinition ${param.name} has no default value`);
                    const newParam = userDefinedParam ?? StringToolParameter.Reusable(param.name, typedParam.defaultValue);
                    if (newParam instanceof ToolVariable) {
                        const paramValue = newParam.getValue(parameters);
                        scopeParams.push(StringToolParameter.Reusable(param.name, paramValue));
                    }
                    else {
                        scopeParams.push(newParam);
                    }
                    break;
                }
                case ToolParameterValueType.NUMBER: {
                    const typedParam = param;
                    if (typedParam.defaultValue === undefined ||
                        typedParam.defaultValue === null ||
                        Number.isNaN(typedParam.defaultValue))
                        throw new Error(`NumberReusableLogicParameterDefinition ${param.name} has no default value`);
                    const newParam = userDefinedParam ?? NumberToolParameter.Reusable(param.name, typedParam.defaultValue);
                    if (newParam instanceof ToolVariable) {
                        const paramValue = newParam.getValue(parameters);
                        scopeParams.push(NumberToolParameter.Reusable(param.name, paramValue));
                    }
                    else {
                        scopeParams.push(newParam);
                    }
                    break;
                }
                case ToolParameterValueType.DATE: {
                    const typedParam = param;
                    if (!typedParam.defaultValue)
                        throw new Error(`DateReusableLogicParameterDefinition ${param.name} has no default value`);
                    const newParam = userDefinedParam ?? DateToolParameter.Reusable(param.name, typedParam.defaultValue);
                    if (newParam instanceof ToolVariable) {
                        const paramValue = newParam.getValue(parameters);
                        scopeParams.push(DateToolParameter.Reusable(param.name, paramValue));
                    }
                    else {
                        scopeParams.push(newParam);
                    }
                    break;
                }
                case ToolParameterValueType.BOOLEAN: {
                    const typedParam = param;
                    if (typedParam.defaultValue === undefined)
                        throw new Error(`BoolReusableLogicParameterDefinition ${param.name} has no default value`);
                    const newParam = userDefinedParam ?? BoolToolParameter.Reusable(param.name, typedParam.defaultValue);
                    if (newParam instanceof ToolVariable) {
                        const paramValue = newParam.getValue(parameters);
                        scopeParams.push(BoolToolParameter.Reusable(param.name, paramValue));
                    }
                    else {
                        scopeParams.push(newParam);
                    }
                    break;
                }
                case ToolParameterValueType.ENUM: {
                    const typedParam = param;
                    if (!typedParam.defaultValue)
                        throw new Error(`EnumReusableLogicParameterDefinition ${param.name} has no default value`);
                    const newParam = userDefinedParam ??
                        EnumToolParameter.Reusable(param.name, typedParam.defaultValue, typedParam.toolEnumType);
                    if (newParam instanceof ToolVariable) {
                        const paramValue = newParam.getValue(parameters);
                        scopeParams.push(EnumToolParameter.Reusable(param.name, paramValue, userDefinedParam?.toolEnumType));
                    }
                    else {
                        scopeParams.push(newParam);
                    }
                    break;
                }
                case ToolParameterValueType.CALENDAR: {
                    const typedParam = param;
                    if (!typedParam.defaultLevel || !typedParam.defaultCalendarID)
                        throw new Error(`CalendarReusableLogicParameterDefinition ${param.name} has no default values`);
                    const newParam = userDefinedParam ??
                        CalendarToolParameter.Reusable(param.name, typedParam.defaultCalendarID, typedParam.defaultLevel);
                    if (newParam instanceof ToolVariable) {
                        const { calendarID, level } = newParam.getValue(parameters);
                        scopeParams.push(CalendarToolParameter.Reusable(param.name, calendarID, level));
                    }
                    else {
                        scopeParams.push(newParam);
                    }
                    break;
                }
                case ToolParameterValueType.COLUMN: {
                    const typedParam = param;
                    if (!typedParam.columnTypes)
                        // if (!typedParam.columnTypes || !typedParam.defaultValue)
                        throw new Error(`ColumnReusableLogicParameterDefinition ${param.name} has no default values`);
                    const newParam = userDefinedParam ??
                        ColumnToolParameter.Reusable(param.name, typedParam.defaultValue ?? '', typedParam.columnTypes);
                    if (newParam instanceof ToolVariable) {
                        const paramValue = newParam.getValue(parameters);
                        scopeParams.push(ColumnToolParameter.Reusable(param.name, paramValue, typedParam.columnTypes));
                    }
                    else {
                        scopeParams.push(newParam);
                    }
                    break;
                }
                default:
                    throw new Error(`Unsupported value type ${param.valueType}`);
            }
        });
        return scopeParams;
    }
};
__decorate([
    JsonConverter(new MapConverter(ReusableLogicInputMapping))
], ReusableLogicConfig.prototype, "inputMap", void 0);
__decorate([
    JsonConverter(new MapConverter())
], ReusableLogicConfig.prototype, "outputMap", void 0);
__decorate([
    JsonConverter(new MultiValueMapConverter([ToolParameter, SimpleToolVariable]))
], ReusableLogicConfig.prototype, "parameterValues", void 0);
ReusableLogicConfig = ReusableLogicConfig_1 = __decorate([
    JsonSchemaV2(reusableLogicConfigSchema, reusableLogicConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.REUSABLE_LOGIC)
], ReusableLogicConfig);
export { ReusableLogicConfig };
//# sourceMappingURL=reusableLogicConfig.js.map